import { Controller } from "@hotwired/stimulus"

// Mobile bottom sheet, content-by-intent (not snap-points).
//   closed     — height 0, hidden; dock is visible below
//   library    — ~60dvh, shows object library
//   properties — ~55dvh, shows properties form for the selected object
//   menu       — ~60dvh, shows insolation+norms tabs
//
// Opens via events dispatched by the dock, the contextual toolbar, or
// long-press on the canvas:
//   mobile-dock:open-library   → openLibrary()
//   mobile-dock:open-menu      → openMenu()
//   object:long-press          → openProperties()
//   library:pending-armed      → close()  (user picked a type; reveal canvas for tap-to-place)
//   selection:clear            → close()  if currently in properties mode
//
// A tap on the handle closes; swipe-down on handle also closes; tap on
// the backdrop (via the ::before pseudo when visible) closes.
const STATES = ["closed", "library", "properties", "menu"]

export default class extends Controller {
  static targets = ["handle", "heading", "panel"]

  connect() {
    if (!this.element.dataset.state) this.element.dataset.state = "closed"
    this.#syncPanels()
    this.boundPointerDown = (e) => this.#onPointerDown(e)
    if (this.hasHandleTarget) {
      this.handleTarget.addEventListener("pointerdown", this.boundPointerDown)
      this.handleTarget.addEventListener("click", () => this.close())
    }
  }

  disconnect() {
    if (this.hasHandleTarget && this.boundPointerDown) {
      this.handleTarget.removeEventListener("pointerdown", this.boundPointerDown)
    }
  }

  openLibrary() { this.#open("library") }
  openProperties() { this.#open("properties") }
  openMenu() { this.#open("menu") }

  close() { this.#setState("closed") }

  // When the user deselects an object (e.g. taps empty canvas), a properties
  // sheet for that object would show stale data — close it. Leave library
  // and menu sheets untouched.
  closeIfProperties() {
    if (this.element.dataset.state === "properties") this.close()
  }

  // Called by the sheet backdrop (::before pseudo element receives a click when
  // positioned fixed with pointer-events: auto). We hook it via a DOM event from
  // a dedicated backdrop div.
  onBackdropClick() { this.close() }

  #open(state) {
    if (!STATES.includes(state) || state === "closed") return
    this.#setState(state)
  }

  #setState(state) {
    this.element.dataset.state = state
    this.#syncPanels()
    this.#syncHandleLabel(state)
    this.dispatch("state", { detail: { state } })
  }

  #syncPanels() {
    const state = this.element.dataset.state
    for (const panel of this.panelTargets) {
      panel.classList.toggle("hidden", panel.dataset.panel !== state)
    }
    if (this.hasHeadingTarget) {
      const titles = {
        library: "Добавить объект",
        properties: "Свойства",
        menu: "Солнце и нормы",
        closed: "",
      }
      this.headingTarget.textContent = titles[state] ?? ""
    }
  }

  #syncHandleLabel(state) {
    if (!this.hasHandleTarget) return
    this.handleTarget.setAttribute(
      "aria-label",
      state === "closed" ? "Панель закрыта" : "Закрыть панель"
    )
  }

  // Swipe-down on handle closes the sheet. No free-height dragging — too easy
  // to misuse when content is tall.
  #onPointerDown(event) {
    if (event.button && event.button !== 0) return
    if (this.element.dataset.state === "closed") return
    const startY = event.clientY
    let closedByGesture = false

    const onMove = (e) => {
      const dy = e.clientY - startY
      if (dy > 60) {
        closedByGesture = true
        document.removeEventListener("pointermove", onMove)
        document.removeEventListener("pointerup", onUp)
        document.removeEventListener("pointercancel", onUp)
        this.close()
      }
    }
    const onUp = () => {
      document.removeEventListener("pointermove", onMove)
      document.removeEventListener("pointerup", onUp)
      document.removeEventListener("pointercancel", onUp)
      if (!closedByGesture) return
      // Swallow the click that would otherwise toggle via the click handler.
      const eat = (ev) => { ev.stopPropagation(); ev.preventDefault() }
      this.handleTarget.addEventListener("click", eat, { capture: true, once: true })
    }
    document.addEventListener("pointermove", onMove)
    document.addEventListener("pointerup", onUp)
    document.addEventListener("pointercancel", onUp)
  }
}
